import React, { createContext, useContext, useState, ReactNode } from 'react';
import { useAuthContext } from '../contexts/AuthContext';

interface AuthDebugContextType {
    showAuthDebug: boolean;
    setShowAuthDebug: (show: boolean) => void;
}

const AuthDebugContext = createContext<AuthDebugContextType | undefined>(undefined);

export const useAuthDebugContext = () => {
    const context = useContext(AuthDebugContext);
    // Return a safe default if context is not available (instead of throwing)
    if (!context) {
        return {
            showAuthDebug: false,
            setShowAuthDebug: () => { }, // No-op function
        };
    }
    return context;
};

export const AuthDebugProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [showAuthDebug, setShowAuthDebug] = useState(false);

    return (
        <AuthDebugContext.Provider value={{ showAuthDebug, setShowAuthDebug }}>
            {children}
        </AuthDebugContext.Provider>
    );
};

/**
 * Debug component to show current authentication state
 * Shows in all environments for Admin and Root users when showAuthDebug is true
 */
export const AuthDebug: React.FC = () => {
    const auth = useAuthContext();
    const { showAuthDebug } = useAuthDebugContext();

    // Check if user is Admin or Root
    const isAdmin = auth.user?.role === 'Admin';
    const isRoot = auth.user?.role === 'Root';
    const shouldShow = (isAdmin || isRoot) && showAuthDebug;

    if (!shouldShow) {
        return null;
    }

    return (
        <div style={{
            position: 'absolute',
            bottom: '10px',
            right: '10px',
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
            padding: '10px',
            borderRadius: '5px',
            fontSize: '12px',
            fontFamily: 'monospace',
            zIndex: 10001, // Above the modal content
            maxWidth: '400px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
        }}>
            <div><strong>Auth Debug:</strong></div>
            <div>Loading: {auth.loading ? 'true' : 'false'}</div>
            <div>Authenticated: {auth.isAuthenticated ? 'true' : 'false'}</div>
            <div>User: {auth.user ? `${auth.user.firstName} ${auth.user.lastName}` : 'null'}</div>
            <div style={{ whiteSpace: 'nowrap' }}>User ID: {auth.user?.userId || 'null'}</div>
            <div style={{ whiteSpace: 'nowrap' }}>Team ID: {auth.user?.teamId || 'null'}</div>
            <div>Access Token: {auth.accessToken ? 'present' : 'null'}</div>
            <div>Refresh Token: {auth.refreshToken ? 'present' : 'null'}</div>
            <div>Error: {auth.error || 'none'}</div>
        </div>
    );
};
